import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RemoteVideoTrack, RemoteAudioTrack, RemoteTrack, RemoteTrackPublication } from "twilio-video";
import { cn } from "@tellescope/ui";
import { SCREEN_VIDEO_TRACK_NAME } from "../hooks/useScreenShare";
import { useTrackAttachment } from "../hooks/useTrackAttachment";
import { RemoteAudioTrackPlayer } from "./RemoteAudioTrackPlayer";
import type { VideoParticipantProps } from "../types";

/**
 * VideoParticipant - Renders a single participant's camera and audio.
 *
 * Shows the participant's camera video (the screen-share track, if any, is lifted to the
 * presentation stage instead). Plays every audio track the participant publishes so that
 * microphone and screen-share audio are heard simultaneously.
 */
export function VideoParticipant({ participant, isLocal = false, resolveIdentity, className }: VideoParticipantProps) {
  const { t } = useTranslation("videoCalling");

  const [cameraTrack, setCameraTrack] = useState<RemoteVideoTrack | null>(null);
  const [audioTracks, setAudioTracks] = useState<RemoteAudioTrack[]>([]);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const displayName = resolveIdentity?.(participant.identity) ?? participant.identity;

  // Subscribe to existing and new tracks
  useEffect(() => {
    const handleTrackSubscribed = (track: RemoteTrack) => {
      if (track.kind === "video") {
        // The shared screen is rendered on the presentation stage, not in the participant tile.
        if (track.name !== SCREEN_VIDEO_TRACK_NAME) {
          setCameraTrack(track);
        }
      } else if (track.kind === "audio") {
        const audioTrack = track;
        setAudioTracks((prev) => (prev.some((t) => t.sid === audioTrack.sid) ? prev : [...prev, audioTrack]));
      }
    };

    const handleTrackUnsubscribed = (track: RemoteTrack) => {
      if (track.kind === "video") {
        if (track.name !== SCREEN_VIDEO_TRACK_NAME) {
          const videoTrack = track;
          setCameraTrack((current) => (current === videoTrack ? null : current));
        }
      } else if (track.kind === "audio") {
        const audioTrack = track;
        setAudioTracks((prev) => prev.filter((t) => t.sid !== audioTrack.sid));
      }
    };

    const handleTrackEnabled = (publication: RemoteTrackPublication) => {
      if (publication.kind === "video" && publication.trackName !== SCREEN_VIDEO_TRACK_NAME) {
        setIsVideoEnabled(true);
      }
    };

    const handleTrackDisabled = (publication: RemoteTrackPublication) => {
      if (publication.kind === "video" && publication.trackName !== SCREEN_VIDEO_TRACK_NAME) {
        setIsVideoEnabled(false);
      }
    };

    // Get existing subscribed tracks
    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed && publication.track) {
        handleTrackSubscribed(publication.track);
      }
    });

    // Subscribe to track events
    participant.on("trackSubscribed", handleTrackSubscribed);
    participant.on("trackUnsubscribed", handleTrackUnsubscribed);
    participant.on("trackEnabled", handleTrackEnabled);
    participant.on("trackDisabled", handleTrackDisabled);

    return () => {
      participant.off("trackSubscribed", handleTrackSubscribed);
      participant.off("trackUnsubscribed", handleTrackUnsubscribed);
      participant.off("trackEnabled", handleTrackEnabled);
      participant.off("trackDisabled", handleTrackDisabled);
    };
  }, [participant]);

  const videoRef = useTrackAttachment<HTMLVideoElement>(cameraTrack);

  return (
    <div className={cn("relative w-full h-full bg-gray-900 rounded-lg overflow-hidden", className)}>
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        muted={isLocal} // Mute local to prevent echo
        playsInline
        className={cn(
          "w-full h-full object-cover",
          isLocal && "scale-x-[-1]", // Mirror local video
          !isVideoEnabled && "hidden"
        )}
      />

      {/* Audio elements (for remote participants only) - one per track so mic + screen audio coexist */}
      {!isLocal && audioTracks.map((track) => <RemoteAudioTrackPlayer key={track.sid} track={track} />)}

      {/* Placeholder when video is disabled */}
      {!isVideoEnabled && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="relative w-1/2 max-w-[200px] aspect-square">
            <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center shadow-lg">
              <span className="text-[4rem] text-gray-700 font-medium select-none">
                {displayName?.charAt(0)?.toUpperCase() ?? "?"}
              </span>
            </div>
            {/* Online status indicator */}
            <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-800" />
          </div>
        </div>
      )}

      {/* Participant label */}
      <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-white text-sm">
        {isLocal ? t("You") : displayName}
      </div>
    </div>
  );
}
